import {
  doc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import { DomainError } from '@/core/errors/DomainError';
import {
  matchSchema,
  type Match,
  type MatchResult,
  type TeamId,
  type TournamentId,
  type Uid,
} from '@/core/schemas';
import { toAppError, type AppError } from '@/infrastructure/errors/AppError';
import { firestore } from '@/infrastructure/firebase/app';
import { createConverter } from '@/infrastructure/firestore/converter';
import { firestorePaths } from '@/infrastructure/firestore/paths';

const converter = createConverter(matchSchema, 'matches');

/**
 * Live subscription to every match of the tournament.
 *
 * One listener for all matches rather than one per view: with at most a few
 * hundred documents that is cheaper than several filtered queries, and every
 * derived view — schedule, groups, bracket, next match — is a pure computation
 * on the same array.
 */
export function subscribeMatches(
  tournamentId: TournamentId,
  onData: (matches: Match[]) => void,
  onError: (error: AppError) => void,
): () => void {
  const matches = query(
    collection(firestore, firestorePaths.matches(tournamentId)).withConverter(converter),
    orderBy('order'),
  );

  return onSnapshot(
    matches,
    (snapshot) => {
      try {
        onData(snapshot.docs.map((document) => document.data()));
      } catch (error) {
        onError(toAppError(error));
      }
    },
    (error) => {
      onError(toAppError(error));
    },
  );
}

/** Which side of the match this captain reports for, or `null` if neither. */
export function sideOfCaptain(match: Match, teamId: TeamId): 'home' | 'away' | null {
  if (match.home.kind === 'team' && match.home.teamId === teamId) return 'home';
  if (match.away.kind === 'team' && match.away.teamId === teamId) return 'away';
  return null;
}

export interface DirectorResult {
  match: Match;
  winnerTeamId: TeamId | null;
  legs: { home: number; away: number };
  actorUid: Uid;
}

/**
 * Sets or corrects a match result as the tournament director.
 *
 * Unlike a captain's report this bypasses the two-report agreement — the
 * director's word is final, so `result.source` is recorded as `director`. Used
 * both to resolve conflicts and to fix an already completed match.
 *
 * Returns the match as it now stands, so the caller can run the bracket
 * progression immediately instead of waiting for the snapshot to come back.
 */
export async function setResultAsDirector(input: DirectorResult): Promise<Match> {
  const { match, winnerTeamId, legs, actorUid } = input;

  if (match.home.kind !== 'team' || match.away.kind !== 'team') {
    throw new DomainError('MATCH_NOT_REPORTABLE', 'Die Teilnehmer stehen noch nicht fest.', {
      matchId: match.id,
    });
  }

  const loserTeamId =
    winnerTeamId === null
      ? null
      : winnerTeamId === match.home.teamId
        ? match.away.teamId
        : match.home.teamId;

  const result: MatchResult = {
    winnerTeamId,
    loserTeamId,
    score: { legs },
    source: 'director',
    confirmedAt: new Date(),
  };

  try {
    await updateDoc(doc(firestore, firestorePaths.match(match.tournamentId, match.id)), {
      status: 'completed',
      result,
      finishedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }

  return { ...match, status: 'completed', result };
}

/** Assigns a board and calls the match up (status `called`). */
export async function callMatch(input: {
  match: Match;
  boardNumber: number;
  actorUid: Uid;
}): Promise<void> {
  const { match, boardNumber, actorUid } = input;

  try {
    await updateDoc(doc(firestore, firestorePaths.match(match.tournamentId, match.id)), {
      boardNumber,
      status: 'called',
      calledAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}

/** Changes only the board number, without touching the match status. */
export async function assignBoard(input: {
  match: Match;
  boardNumber: number | null;
  actorUid: Uid;
}): Promise<void> {
  const { match, boardNumber, actorUid } = input;

  try {
    await updateDoc(doc(firestore, firestorePaths.match(match.tournamentId, match.id)), {
      boardNumber,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}
